import React, { useMemo, useState } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import gql from 'graphql-tag'

import getNameOfGqlQuery from 'helpers/getNameOfGqlQuery'
import { downloadFromUrl } from 'helpers/downloadFromUrl'
import ContentBlock from 'components/ContentBlock'
import { Form, Select, DateTimePicker } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { AlertModal, WarningModal } from 'components/Modal'
import { useMutation, useLazyQuery } from 'components/graphql'
import ProgressBar from 'components/ProgressBar/ProgressBar'

import { exportDSFin as exportDSFinMutation, getDSFinExport } from '../../gql/exportDSFin'


const queryCrs = gql`
	query listCashRegisters {
		cashAssist {
			listCashRegisters(activeOnly: true, params: {
				sort: [{ field: "name", order: ASC }]
			}) {
				id
				name
			}
		}
	}
`

const Dsfin = (props) => {
	const [isDSFSubmitting, setIsDSFSubmitting] = useState(false)
	const [isDSFError, setIsDSFError] = useState(false)
	const [showNoDataModal, setShowNoDataModal] = useState(false)
	const [t] = useTranslation()
	const startDay = useMemo(() => moment().utc().startOf('day').toISOString(), [])
	const endDay = useMemo(() => moment().utc().endOf('day').toISOString(), [])

	const handleDSFinError = (error) => {
		if(error === 'No data') {
			return setShowNoDataModal(true)
		}
		setIsDSFSubmitting(false)
		setIsDSFError(true)
	}

	const [getDSFinExportData, DSFinExportRes] = useLazyQuery(getDSFinExport, {
		pollInterval: 2000,
		fetchPolicy: 'network-only',
		onError: handleDSFinError,
		onCompleted: res => {
			const { status, error, file } = res?.cashAssist?.getDSFinExport || {}
			if(status === 'READY' || status === 'ERROR') {
				DSFinExportRes.stopPolling()
				setIsDSFSubmitting(false)
			}
			if(file?.url) {
				return downloadFromUrl(file.url)
			}
			if(error) {
				return handleDSFinError(error)
			}
		}
	})
	const { status, progress } = DSFinExportRes.data?.getDSFinExport || {}

	const [exportDSFin] = useMutation(exportDSFinMutation, {
		onCompleted: data => {
			const mutationName = getNameOfGqlQuery(exportDSFinMutation)
			getDSFinExportData({ variables: { requestId: data?.[mutationName] } })
		},
		onError: handleDSFinError
	})

	const handleSubmitDSF = vals => {
		setIsDSFSubmitting(true)
		exportDSFin({
			variables: {
				params: vals
			}
		})
	}

	return (
		<ContentBlock label={t('options_section.strings.dsfin')}>
			<Form initialValues={{ period: { from: startDay, to: endDay } }} onSubmit={handleSubmitDSF}>
				<Row>
					<Col xs>
						<Select
							field='cashRegisterId'
							label={t('common.fields.cash_register')}
							remoteOptions={{
								query: queryCrs
							}}
							valueField='id'
							labelField='name'
							isSearchable
							validate={val => val ? null : t('validators.required')}
							validateOnChange
						/>
					</Col>
					<Col xs>
						<DateTimePicker
							field='period'
							label={t('common.fields.date')}
							validate={val => val ? null : t('validators.required')}
							validateOnChange
							interval
						/>
					</Col>
				</Row>
				<Divider />
				<Row middle='xs'>
					<Col xs={2}>
						<Button
							type='submit'
							text={t('common.buttons.apply')}
							loading={isDSFSubmitting}
						/>
					</Col>
					{status === 'LOADING' &&
						<Col xs={1}>
							<ProgressBar progress={progress} width={120}/>
						</Col>
					}
				</Row>
			</Form>
			{isDSFError &&
				<AlertModal
					onClose={() => setIsDSFError(false)}
					text={`${t('common.strings.table_no_data')} ${t('common.strings.or')} ${t('common.strings.network_error')}.`}
				/>
			}
			{showNoDataModal &&
				<WarningModal
					onClose={() => setShowNoDataModal(false)}
					text={`${t('common.strings.table_no_data')}.`}
				/>
			}
		</ContentBlock>
	)
}

export default Dsfin
